import { useState } from 'react';
import { Group, SimpleGrid, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { DataTable, MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { DonutChart } from '@repletoryreach/sdk-ui/charts';
import { BarList } from './BarList';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import type { insights } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { answerSplit, countBy } from '../lib/data';
import { Bullets, MUTED, Muted, Panel, WholeReport, outcomeLabel, pct } from '../shared';
import type { TabProps } from './tab';

// What each kind of start is called. A start not listed shows under its own code.
const STARTS = ['keyword', 'intent', 'greeting', 'first_contact', 'default', 'offer', 'switch', 'api'];

/**
 * How the chatbot did: the customers' questions answered, partly or not (the colours mean good, so-so and bad, and
 * each slice is named); what happened in the chats; every flow, how it was started and how far runs got; the AI
 * answers it was sure and not sure of; and the AI's assessment.
 */
export function Chatbot({ report, rows }: TabProps) {
  const t = useT();
  const split = answerSplit(rows);
  const asked = split.yes + split.partly + split.no;
  const outcomes = countBy(rows.map((r) => r.outcome));
  const flows = report.metrics?.flows ?? [];
  const [flowId, setFlowId] = useState('');
  const flow = flows.find((f) => f.flow_id === flowId) ?? flows[0];
  const ai = report.metrics?.ai_answers;
  const deep = report.metrics?.deep_review;
  const bot = report.sections?.bot;

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        <Panel title={t('insights.bot.questions')} note={t('insights.bot.questionsNote', { count: formatCount(asked) })}>
          {asked === 0 ? <Muted>{t('insights.empty.filtered')}</Muted> : (
            <>
              <DonutChart size={170} h={250} data={[
                { name: t('insights.answered.yes'), value: split.yes, color: 'var(--rr-color-success)' },
                { name: t('insights.answered.partly'), value: split.partly, color: 'var(--rr-color-warning)' },
                { name: t('insights.answered.no'), value: split.no, color: 'var(--rr-color-error)' }
              ].filter((x) => x.value > 0)} valueFormatter={(v) => formatCount(v)} />
              <Text role="caption" c={MUTED} data-testid="insights-answer-split">
                {t('insights.bot.split', { yes: pct(split.yes, asked), partly: pct(split.partly, asked), no: pct(split.no, asked) })}
              </Text>
            </>
          )}
        </Panel>
        <Panel title={t('insights.bot.outcomes')}>
          {outcomes.length === 0 ? <Muted>{t('insights.empty.filtered')}</Muted> : (
            <BarList label={t('insights.bot.outcomes')} items={outcomes.map((o) => ({ name: outcomeLabel(t, o.name), value: o.count }))} />
          )}
        </Panel>
      </SimpleGrid>

      <Panel title={t('insights.bot.flows')} note={t('insights.bot.flowsNote')} testId="insights-flows">
        {flows.length === 0 ? <Muted>{t('insights.bot.noFlows')}</Muted> : (
          <>
            <div className="rr-insights__table-scroll">
              <DataTable<Indexed<insights.InsightFlowStat>>
                records={flows as Indexed<insights.InsightFlowStat>[]}
                idAccessor="flow_id"
                columns={[
                  { accessor: 'name', title: t('insights.bot.flow'), render: (f) => <Text role="bodySmall" fw={600}>{f.name}</Text> },
                  { accessor: 'starts', title: t('insights.bot.starts'), textAlign: 'right', render: (f) => formatCount(f.starts) },
                  { accessor: 'by', title: t('insights.bot.startedBy'), render: (f) => (
                    <Text role="caption" c={MUTED}>
                      {Object.entries(f.by).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${t(STARTS.includes(k) ? `insights.start.${k}` : k, k)} ${formatCount(n)}`).join(' · ')}
                    </Text>
                  ) },
                  { accessor: 'finished', title: t('insights.bot.finished'), textAlign: 'right', render: (f) => formatCount(f.finished) },
                  { accessor: 'handed_over', title: t('insights.bot.handedOver'), textAlign: 'right', render: (f) => formatCount(f.handed_over) },
                  { accessor: 'stopped', title: t('insights.bot.stopped'), textAlign: 'right', render: (f) => formatCount(f.stopped) }
                ]}
              />
            </div>
            {flow && flow.funnel.length > 0 && (
              <Stack gap="xs">
                <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
                  <Title order={5}>{t('insights.bot.funnel', { name: flow.name })}</Title>
                  {flows.length > 1 && (
                    <Select size="xs" label={t('insights.bot.flow')} value={flow.flow_id} onChange={(e) => setFlowId(e.currentTarget.value)}
                      data={flows.filter((f) => f.funnel.length > 0).map((f) => ({ value: f.flow_id, label: f.name }))} />
                  )}
                </Group>
                <BarList label={t('insights.bot.reached')} testId="insights-funnel" items={flow.funnel.map((s) => ({ name: s.label, value: s.reached }))} />
                <Text role="caption" c={MUTED}>{t('insights.bot.funnelNote')}</Text>
              </Stack>
            )}
          </>
        )}
      </Panel>

      {ai && (
        <Panel title={t('insights.bot.ai')} note={t('insights.bot.aiNote')}>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <MetricCard label={t('insights.bot.aiGiven')} value={formatCount(ai.given)} />
            <MetricCard label={t('insights.bot.aiSure')} value={formatCount(ai.answered)} hint={`${pct(ai.answered, ai.given)}%`} />
            <MetricCard label={t('insights.bot.aiNotSure')} value={formatCount(ai.not_sure)} hint={`${pct(ai.not_sure, ai.given)}%`} />
          </SimpleGrid>
          {ai.not_sure_questions.length > 0 && (
            <Stack gap={4}>
              <Title order={5}>{t('insights.bot.notSureQuestions')}</Title>
              <Bullets items={ai.not_sure_questions.slice(0, 10).map((q) => t('insights.bot.notSureLine', { question: q.question, count: formatCount(q.count) }))} />
            </Stack>
          )}
        </Panel>
      )}

      {deep && (
        <Panel title={t('insights.bot.deep')} note={t('insights.bot.deepNote')}>
          <Text role="bodySmall">
            {Object.entries(deep.counts).filter(([, n]) => n > 0).map(([v, n]) => `${t(`flows.coach.verdict.${v}`, v)}: ${formatCount(n)}`).join(' · ')}
          </Text>
        </Panel>
      )}

      {bot && (
        <Panel title={t('insights.bot.assessment')} note={<WholeReport t={t} />} testId="insights-assessment">
          <Text role="bodySmall">{bot.assessment}</Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap={4}>
              <Title order={5}>{t('insights.bot.strengths')}</Title>
              <Bullets items={bot.strengths} />
            </Stack>
            <Stack gap={4}>
              <Title order={5}>{t('insights.bot.weaknesses')}</Title>
              <Bullets items={bot.weaknesses} />
            </Stack>
          </SimpleGrid>
        </Panel>
      )}
    </Stack>
  );
}
